import React, { useState } from 'react';
import SafePlyr from './SafePlyr';
import LoadingSpinner from './LoadingSpinner';
import '../styles/global.css';

import localAudios from '../data/localAudios.json';

const AudioLibrary = () => {
    const [audios] = useState(localAudios);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    const handlePlay = (audio) => {
        setCurrentAudio(audio);
    };

    const folders = [...new Set(audios.map(a => a.area || 'General'))].sort();

    const displayedAudios = selectedFolder
        ? audios.filter(a => (a.area || 'General') === selectedFolder)
        : [];

    const sortNatural = (arr) => [...arr].sort((a, b) => {
        const titleA = a.title || a.nombre || '';
        const titleB = b.title || b.nombre || '';
        const numA = parseFloat(titleA.match(/^[\d.]+/)?.[0] || titleA);
        const numB = parseFloat(titleB.match(/^[\d.]+/)?.[0] || titleB);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return titleA.localeCompare(titleB, undefined, { numeric: true });
    });

    const finalFilteredAudios = sortNatural((selectedFolder ? displayedAudios : []).filter(a =>
        (a.title || a.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())
    ));

    if (loading) return (
        <div style={{ padding: '20px', color: 'var(--text-primary)', textAlign: 'center' }}>
            <div className="loading-spinner"></div>
            <p>Cargando biblioteca de audio...</p>
            <LoadingSpinner label="Cargando datos..." height="300px" />
        </div>
    );

    return (
        <div className="audio-library" style={{ padding: '20px', color: 'var(--text-primary)', paddingBottom: '100px' }}>
            <div className="flex-between" style={{ marginBottom: '20px', alignItems: 'center' }}>
                <div>
                    <h1 style={{ color: 'var(--accent)', margin: 0, fontSize: '2.5rem', fontWeight: '800' }}>
                        Repositorio de Audio 🎧
                    </h1>
                    <p style={{ opacity: 0.6, marginTop: '5px' }}>
                        {selectedFolder ? `Carpeta: ${selectedFolder}` : 'Seleccione una unidad temática estratégica'}
                    </p>
                </div>
                {selectedFolder && (
                    <button
                        onClick={() => setSelectedFolder(null)}
                        className="btn-secondary"
                        style={{ padding: '8px 16px', borderRadius: '12px' }}
                    >
                        ⬅ Volver a Carpetas
                    </button>
                )}
            </div>

            {/* FOLDER SELECTION VIEW */}
            {!selectedFolder && (
                <div className="folder-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '25px',
                    marginTop: '30px'
                }}>
                    {folders.map(folder => {
                        const count = audios.filter(a => (a.area || 'General') === folder).length;
                        return (
                            <div
                                key={folder}
                                className="card fold-card"
                                onClick={() => setSelectedFolder(folder)}
                                style={{
                                    padding: '30px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: 'var(--glass-bg)',
                                    border: '1px solid var(--glass-border)',
                                    borderRadius: '24px',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-10px)';
                                    e.currentTarget.style.borderColor = 'var(--accent)';
                                    e.currentTarget.style.boxShadow = 'var(--neon-glow)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'none';
                                    e.currentTarget.style.borderColor = 'var(--glass-border)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div style={{ fontSize: '4rem', marginBottom: '15px' }}>📂</div>
                                <h3 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '8px' }}>{folder}</h3>
                                <span style={{
                                    background: 'rgba(0, 242, 254, 0.1)',
                                    color: 'var(--accent)',
                                    padding: '4px 12px',
                                    borderRadius: '50px',
                                    fontSize: '0.85rem',
                                    fontWeight: '600'
                                }}>
                                    {count} {count === 1 ? 'Audio' : 'Audios'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* SEARCH AND AUDIO LIST VIEW */}
            {selectedFolder && (
                <>
                    <div className="search-container" style={{ marginBottom: '30px' }}>
                        <input
                            type="text"
                            placeholder={`🔍 Buscar en ${selectedFolder}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="form-input"
                            style={{
                                width: '100%',
                                padding: '15px 25px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                color: 'white',
                                border: '1px solid var(--glass-border)',
                                borderRadius: '50px',
                                outline: 'none'
                            }}
                        />
                    </div>

                    <div className="audio-list" style={{ display: 'grid', gap: '12px' }}>
                        {finalFilteredAudios.length === 0 ? (
                            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                                <p style={{ color: 'var(--text-muted)' }}>No se encontraron audios en esta unidad.</p>
                            </div>
                        ) : (
                            finalFilteredAudios.map(audio => (
                                <div
                                    key={audio.id}
                                    className={`audio-item ${currentAudio?.id === audio.id ? 'active' : ''}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '15px 20px',
                                        background: currentAudio?.id === audio.id
                                            ? 'linear-gradient(90deg, rgba(0, 242, 254, 0.15) 0%, rgba(0, 210, 235, 0.05) 100%)'
                                            : 'var(--glass-bg)',
                                        border: currentAudio?.id === audio.id ? '1px solid var(--accent)' : '1px solid var(--glass-border)',
                                        borderRadius: '16px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => handlePlay(audio)}
                                >
                                    <div style={{
                                        width: '44px',
                                        height: '44px',
                                        background: currentAudio?.id === audio.id ? 'var(--accent)' : 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '15px',
                                        fontSize: '1.2rem',
                                        color: currentAudio?.id === audio.id ? '#0a0e17' : 'white'
                                    }}>
                                        {currentAudio?.id === audio.id ? '🔊' : '▶️'}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{ fontSize: '1rem', margin: 0, fontWeight: '600', color: currentAudio?.id === audio.id ? 'var(--accent)' : 'white' }}>
                                            {audio.title || audio.nombre}
                                        </h3>
                                        <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>Fragmento Educativo Vortex</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            {/* PLYR PLAYER FIXED BOTTOM */}
            {currentAudio && (
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: '#1a1b26', // Dark background specifically for audio
                    padding: '20px',
                    borderTop: '1px solid var(--accent)',
                    zIndex: 2000,
                    boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
                    animation: 'slideUp 0.3s ease-out'
                }}>
                    <div style={{
                        maxWidth: '900px',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '5px'
                        }}>
                            <span style={{ color: 'white', fontWeight: '600' }}>
                                🎵 Reproduciendo: <span style={{ color: 'var(--accent)' }}>{currentAudio.title || currentAudio.nombre}</span>
                            </span>
                            <button
                                onClick={() => setCurrentAudio(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#666',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem'
                                }}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="plyr-wrapper" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                            <SafePlyr
                                source={{
                                    type: 'audio',
                                    sources: [
                                        {
                                            src: currentAudio.url || currentAudio.dropboxUrl,
                                            // Detectar MIME type dinámicamente según extensión del archivo
                                            type: (() => {
                                                const url = currentAudio.url || currentAudio.dropboxUrl || '';
                                                const ext = url.split('.').pop().toLowerCase().split('?')[0];
                                                const mimeMap = {
                                                    'aac': 'audio/aac',
                                                    'acc': 'audio/aac',
                                                    'mp3': 'audio/mpeg',
                                                    'ogg': 'audio/ogg',
                                                    'wav': 'audio/wav',
                                                    'm4a': 'audio/mp4',
                                                    'flac': 'audio/flac',
                                                };
                                                return mimeMap[ext] || 'audio/mpeg';
                                            })(),
                                        },
                                    ],
                                }}
                                options={{
                                    autoplay: true,
                                    controls: [
                                        'play-large', 'restart', 'rewind', 'play', 'fast-forward',
                                        'progress', 'current-time', 'duration', 'mute', 'volume',
                                        'captions', 'settings', 'pip', 'airplay', 'download'
                                    ],
                                    speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] }
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                
                /* Custom Plyr Theme adjustments */
                :root {
                    --plyr-color-main: var(--accent);
                }
                .plyr--audio .plyr__controls {
                    background: transparent;
                    color: white;
                }
                .plyr--audio .plyr__control--overlaid-pause, 
                .plyr--audio .plyr__control--overlaid-play {
                    background: var(--accent);
                }

                @media (max-width: 640px) {
                    .folder-grid {
                        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)) !important;
                        gap: 12px !important;
                        margin-top: 16px !important;
                    }
                    .fold-card {
                        padding: 16px !important;
                        border-radius: 12px !important;
                    }
                    .fold-card div:first-child {
                        font-size: 2rem !important;
                        margin-bottom: 8px !important;
                    }
                    .fold-card h3 {
                        font-size: 0.75rem !important;
                        margin-bottom: 4px !important;
                    }
                    .fold-card span {
                        font-size: 0.6rem !important;
                        padding: 2px 8px !important;
                    }
                    .audio-library h1 {
                        font-size: 1.3rem !important;
                    }
                    .audio-library .flex-between {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 8px !important;
                    }
                    .audio-item {
                        padding: 10px 12px !important;
                        border-radius: 10px !important;
                    }
                    .audio-item div:first-child {
                        width: 32px !important;
                        height: 32px !important;
                        font-size: 0.9rem !important;
                        margin-right: 10px !important;
                    }
                    .audio-item h3 {
                        font-size: 0.8rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default AudioLibrary;
